import React, { FC } from "react";
import { Modal, notification, Select } from "antd";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import Title from "antd/es/typography/Title";
import { FormInput } from "shared/ui";
import { Colors } from "shared/themes";
import { handleRequestError } from "shared/helpers";
import {
  UpdateUseritem,
  useCreateNewUserMutation,
  useGetUsersQuery,
  UserItem,
} from "shared/api";
import { ModalSizeValue } from "shared/constants";

interface AddUserModalProps {
  isOpenNewUserModal: boolean;
  closeAddUserModal: () => void;
  userRolesList: {
    value: number | undefined;
    label: string;
  }[];
}

const AddUserModal: FC<AddUserModalProps> = ({
  isOpenNewUserModal,
  closeAddUserModal,
  userRolesList,
}) => {
  const {
    values,
    resetForm,
    isValid,
    handleChange,
    handleBlur,
    errors,
    setFieldValue,
  } = useFormikContext<UserItem>();

  const { refetch } = useGetUsersQuery({});

  const closeModal = () => {
    closeAddUserModal();
    resetForm();
  };

  const [createNewUser] = useCreateNewUserMutation();
  const [api, contextHolder] = notification.useNotification();
  const { t } = useTranslation();

  const onAddUser = async (params: UserItem) => {
    try {
      const formattedData: UpdateUseritem = {
        ...params,
        roles: params.roles.map((role) => Number(role.id)),
      };
      await createNewUser(formattedData).unwrap();
      api.success({
        message: t("users-page.success-title"),
        description: t("users-page.success-add-desc"),
      });
      closeModal();
      refetch();
    } catch (error) {
      const description = handleRequestError(
        error,
        t("users-page.error-title")
      );

      api.error({
        message: t("users-page.error-title"),
        description,
      });
    }
  };

  return (
    <>
      {contextHolder}

      <Modal
        title={t("add-user-modal.add-user")}
        open={isOpenNewUserModal}
        width={ModalSizeValue.PRIMARY}
        okButtonProps={{
          disabled: !isValid,
          style: {
            background: `${Colors.blue.primary}`,
          },
        }}
        onCancel={closeModal}
        onOk={() => {
          onAddUser(values);
        }}
      >
        <Title level={5} className="text-[14px]">
          {t("add-user-modal.first-name")}
        </Title>

        <FormInput
          value={values.first_name}
          fieldName="first_name"
          placeholder="modal-placeholders.firstname-placeholder"
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.first_name}
          isError={!!errors.first_name}
        />

        <Title level={5} className="text-[14px]">
          {t("add-user-modal.last-name")}
        </Title>

        <FormInput
          value={values.last_name}
          fieldName="last_name"
          placeholder="modal-placeholders.lastname-placeholder"
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.last_name}
          isError={!!errors.last_name}
        />

        <Title level={5} className="text-[14px]">
          {t("add-user-modal.username")}
        </Title>

        <FormInput
          value={values.username}
          fieldName="username"
          placeholder="modal-placeholders.username-placeholder"
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.username}
          isError={!!errors.username}
        />

        <Title level={5} className="text-[14px]">
          {t("Roles")}
        </Title>
        <Select
          mode="multiple"
          value={values.roles.map((role) => role.id)}
          onChange={(selectedIds) => {
            const selectedRoles = userRolesList
              .filter((role) => selectedIds.includes(role.value))
              .map((role) => ({
                id: Number(role.value) || 0,
                name: role.label,
              }));

            setFieldValue("roles", selectedRoles);
          }}
          options={userRolesList}
          placeholder="Select roles"
          style={{ width: "100%" }}
        />

        <Title level={5} className="text-[14px]">
          {t("add-user-modal.email")}
        </Title>

        <FormInput
          value={values.email}
          fieldName="email"
          placeholder="modal-placeholders.email-placeholder"
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.email}
          isError={!!errors.email}
        />

        <Title level={5} className="text-[14px]">
          {t("Are you superuser?")}
        </Title>

        <Select
          value={values.is_superuser ? "Yes" : "No"}
          onChange={(value) => setFieldValue("is_superuser", value === "Yes")}
          options={[
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ]}
        />
      </Modal>
    </>
  );
};

export { AddUserModal };
